import React, { useContext, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
  TextField
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { CartContext } from 'contexts/CartContext'
import SectionHeader from 'components/SectionHeader'
import FixedWidthContainer from 'components/FixedWidthContainer'
import { useTranslation } from 'react-i18next'

const QuoteListPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { quotes, clearQuote, updateQuoteItem } = useContext(CartContext)
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null)

  const { register, handleSubmit, reset } = useForm<{
    poNumber: string
  }>({
    defaultValues: {
      poNumber: ''
    }
  })

  const handleStartEditPoNumber = (
    quoteId: string,
    currentPoNumber: string
  ) => {
    setSelectedQuoteId(quoteId)
    reset({ poNumber: currentPoNumber || '' })
  }

  const handleCancelEditPoNumber = () => {
    setSelectedQuoteId(null)
    reset({ poNumber: '' })
  }

  const onSubmitPoNumber = (poNumber: string, quoteId: string) => {
    updateQuoteItem({
      quoteId,
      poNumber
    })
    setSelectedQuoteId(null)
    reset({ poNumber: '' })
  }

  return (
    <FixedWidthContainer sx={{ my: '50px' }}>
      <SectionHeader title={t('quote.listTitle')} />

      {(!quotes || quotes.length === 0) && (
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Typography variant='h4' color='text.secondary' gutterBottom>
            {t('quote.notFound')}
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {quotes?.map(quote => {
          const isEditingThisPoNumber = selectedQuoteId === quote.id

          return (
            <Grid item xs={12} key={quote.id}>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                p={2}
                sx={{ border: '1px solid #ccc', borderRadius: 2 }}
              >
                <Box display='flex' alignItems='center' gap={1}>
                  {isEditingThisPoNumber ? (
                    <form
                      onSubmit={handleSubmit(data =>
                        onSubmitPoNumber(data.poNumber, quote.id)
                      )}
                    >
                      <Box display='flex' alignItems='center' gap={1}>
                        <TextField
                          size='small'
                          label={t('quote.poNumber')}
                          {...register('poNumber')}
                        />

                        <IconButton color='primary' type='submit'>
                          <CheckIcon />
                        </IconButton>

                        <IconButton
                          color='inherit'
                          onClick={handleCancelEditPoNumber}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </form>
                  ) : (
                    <>
                      <Typography variant='h4'>
                        {t('quote.poNumber')}: {quote.poNumber || '___'}
                      </Typography>
                      <IconButton
                        onClick={() =>
                          handleStartEditPoNumber(
                            quote.id,
                            quote.poNumber || ''
                          )
                        }
                      >
                        <EditIcon />
                      </IconButton>
                    </>
                  )}
                </Box>

                <Box display='flex' gap='16px'>
                  <Button
                    variant='outlined'
                    onClick={() => clearQuote(quote.id)}
                    sx={{ minWidth: 140 }}
                  >
                    {t('quote.deleteThisQuote')}
                  </Button>
                  <Button
                    variant='contained'
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                    sx={{ minWidth: 140 }}
                  >
                    {t('quote.editThisQuote')}
                  </Button>
                </Box>
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </FixedWidthContainer>
  )
}

export default QuoteListPage
